import { useQuery } from "@tanstack/react-query";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { parseLeadsSearchParams } from "common/helpers/utils";
import type { LeadsSearchParams, SelectedFacetParam } from "common/types";

async function getLeadsInfoRequest(
  workspaceId: string,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
): Promise<{
  totalLeads: number;
  isSyncing: boolean;
  accountsSync: Record<string, boolean>;
}> {
  const {
    total_leads: totalLeads,
    sync: isSyncing,
    accounts_sync: accountsSync,
  } = await post(
    `workspaces/${workspaceId}/leads-search-total`,
    parseLeadsSearchParams(searchParams),
  );

  return { totalLeads, isSyncing, accountsSync };
}

export default function useLeadsInfo(
  searchParams: LeadsSearchParams<SelectedFacetParam>,
) {
  const { id: workspaceId, accounts_count: accountsCount } =
    useSelectedWorkspaceContext();

  const {
    data: leadsInfo,
    isFetching: isFetchingLeadsInfo,
    error: leadsInfoError,
    refetch: refetchLeadsInfo,
  } = useQuery({
    queryKey: getQueryKeys(workspaceId).leadsKeys.listInfo(searchParams),
    queryFn: () => getLeadsInfoRequest(workspaceId, searchParams),
    enabled: accountsCount > 0,
  });

  return {
    leadsInfo,
    isFetchingLeadsInfo,
    leadsInfoError,
    refetchLeadsInfo,
  };
}
